import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CtaButton } from "@/components/ui/CtaButton";
import { Upload, FileText, Image as ImageIcon, Presentation, Gamepad2, ScrollText, Trash2, ExternalLink, Search, Share2 } from "lucide-react";

const KIND_ICONS = {
  pdf: FileText, doc: FileText, image: ImageIcon, ppt: Presentation,
  game: Gamepad2, quiz: ScrollText, whiteboard: Presentation,
};

/**
 * /teacher/library — Centralised asset library (PDFs, slides, images, etc.)
 * Teachers upload reusable resources here and can mark them shared (visible
 * to all teachers in their institute). Students/parents see only shared ones.
 */
export default function AILibrary() {
  const [params] = useSearchParams();
  const subjectId = params.get("subjectId") || "";
  const subjectName = params.get("subject") || "";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [kindFilter, setKindFilter] = useState(params.get("kind") || "");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => { load(); }, [kindFilter, filter, subjectId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kindFilter) params.kind = kindFilter;
      if (filter) params.q = filter;
      if (subjectId) params.subject_id = subjectId;
      const { data } = await api.get("/library", { params });
      setItems(data.assets || []);
    } catch { toast.error("Failed to load library"); }
    finally { setLoading(false); }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size / (1024 * 1024) > 100) { toast.error("Max 100 MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name);
    fd.append("kind", "doc"); // backend auto-detects from mime
    fd.append("description", "");
    fd.append("tags", "");
    fd.append("is_shared", "false");
    if (subjectId) fd.append("subject_id", subjectId);
    try {
      await api.post("/library/upload", fd);
      toast.success(`Uploaded · ${file.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const toggleShare = async (a) => {
    try {
      await api.patch(`/library/${a.id}`, { is_shared: !a.is_shared });
      toast.success(a.is_shared ? "Now private" : "Shared with institute");
      load();
    } catch { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try { await api.delete(`/library/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const KINDS = ["", "pdf", "ppt", "image", "doc", "game", "quiz", "whiteboard"];
  const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ library{subjectName ? ` · ${subjectName}` : ""}</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">AI Library</h1>
          <p className="text-sm text-neutral-500 mt-1">Reusable PDFs, slides, images, and game decks. Insert into any class.{subjectId ? " · Scoped to this subject." : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" onChange={onUpload} className="hidden" data-testid="lib-file-input" />
          <CtaButton onClick={onPickFile} disabled={uploading} variant="primary" size="md" data-testid="lib-upload-btn" className="rounded-md">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload asset"}
          </CtaButton>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 bg-white border border-black/8 rounded-xl p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            placeholder="Search by name…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="lib-search"
            className="w-full pl-8 pr-3 py-2 text-sm border border-neutral-200 rounded-md outline-none focus:border-[#0A0C10]"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {KINDS.map((k) => (
            <button
              key={k || "all"}
              onClick={() => setKindFilter(k)}
              data-testid={`lib-filter-${k || "all"}`}
              className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1.5 rounded-md ${kindFilter === k ? "bg-[#0A0C10] text-white" : "border border-neutral-300 hover:border-[#0A0C10]"}`}
            >{k || "all"}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-neutral-300 rounded-xl p-10 text-center">
          <Upload size={28} className="mx-auto text-neutral-400" />
          <p className="text-sm text-neutral-600 mt-3">No assets yet. Upload your first resource to start your library.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lib-grid">
          {items.map((a) => {
            const Icon = KIND_ICONS[a.kind] || FileText;
            return (
              <div key={a.id} className="bg-white border border-black/8 rounded-xl p-4 group hover:border-[#0A0C10] transition" data-testid={`lib-card-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#FBFAF7] rounded-md flex items-center justify-center"><Icon size={18} className="text-[#0A0C10]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{a.name}</div>
                    <div className="text-[11px] font-mono uppercase text-neutral-500">{a.kind} · {a.size_mb} MB</div>
                    {a.description && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{a.description}</p>}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex gap-1">
                    {a.is_shared && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">shared</span>}
                    {(a.tags || []).slice(0, 2).map((t) => <span key={t} className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded">{t}</span>)}
                  </div>
                  <div className="flex items-center gap-1">
                    {a.url && (
                      <a href={`${BACKEND}/api${a.url}`} target="_blank" rel="noreferrer" title="Open" className="text-neutral-500 hover:text-[#0A0C10] p-1" data-testid={`lib-open-${a.id}`}>
                        <ExternalLink size={14} />
                      </a>
                    )}
                    <button onClick={() => toggleShare(a)} title={a.is_shared ? "Make private" : "Share with institute"} className="text-neutral-500 hover:text-[#0A0C10] p-1" data-testid={`lib-share-${a.id}`}>
                      <Share2 size={14} />
                    </button>
                    <button onClick={() => del(a.id)} title="Delete" className="text-red-500 hover:text-red-700 p-1" data-testid={`lib-del-${a.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
